import { orderBy, throttle } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { env } from '@fiverr-private/futile/lib/environment';
import { NotificationSound } from '../NotificationSound';
import {
    ECHO_TABS,
    ECHO_TABS_RESPONSE,
    ON_ECHO_REPORTED_FN_NAME,
    RT_BROADCAST_CHANNEL,
    THROTTLE_SOUND,
} from './constants';
import { Page } from './lib';

interface TabResult {
    isFocused: boolean;
    createdAt: number;
    tabId: string;
}

interface BroadcastData {
    type: string;
    uid?: string;
    callbackFnName?: string;
    tabId?: string;
    createdAt?: number;
    isFocused?: boolean;
    [key: string]: any;
}

interface QueueItem {
    uid: string;
    data: BroadcastData;
    callbackFnName: string;
}

type EventHandler = (event: BroadcastData) => void;
type UnsubscribeFunction = () => void;
type CallbackFunction = (results: TabResult[]) => void;

export class TabManager {
    private _queue: { [key: string]: BroadcastData[] } = {};
    private _queueKickedOff: { [key: string]: boolean } = {};
    private _gotResponse: { [key: string]: boolean } = {};
    private _callbacks: { [key: string]: { [key: string]: CallbackFunction } } = {};
    private _handlers: EventHandler[] = [];
    private _page!: Page;
    private _createdAt!: number;
    private _tabId!: string;
    private _channel?: BroadcastChannel;

    constructor(page: Page) {
        // If not in browser do nothing.
        if (!env.browser) {
            return;
        }

        this._page = page;
        this._createdAt = Date.now();
        this._tabId = `${uuidv4()}`;

        if ('BroadcastChannel' in window) {
            this._channel = new BroadcastChannel(RT_BROADCAST_CHANNEL);
            this._channel.onmessage = this._onBroadcastMessage;
        }
    }

    static create(): TabManager {
        const page = new Page();
        return new TabManager(page);
    }

    isFocused = (): boolean => this._page.hasFocus();

    subscribe = (handler: EventHandler): UnsubscribeFunction => {
        this._handlers.push(handler);

        return () => {
            const index = this._handlers.indexOf(handler);
            this._handlers.splice(index, 1);
        };
    };

    broadcast = (data: BroadcastData): void => {
        if (!this._channel) {
            return;
        }

        this._channel.postMessage(data);
    };

    private _shouldPlaySound = (): Promise<boolean> =>
        new Promise((resolve) => {
            // If browser don't support tabs communication then play sound by default.
            if (!this._channel) {
                resolve(true);
                return;
            }

            if (this.isFocused()) {
                resolve(true);
                return;
            }

            const uid = uuidv4();

            this._gotResponse[uid] = false;

            if (!this._callbacks[uid]) {
                this._callbacks[uid] = {};
            }

            this._callbacks[uid][ON_ECHO_REPORTED_FN_NAME] = (results: TabResult[]) => {
                this._gotResponse[uid] = true;

                if (this._anotherTabIsOpen(results)) {
                    resolve(false);
                    return;
                }

                if (this._currentTabShouldPlaySound(results)) {
                    resolve(true);
                    return;
                }

                resolve(false);
            };

            this._channel.postMessage({ type: ECHO_TABS, uid, callbackFnName: ON_ECHO_REPORTED_FN_NAME });

            setTimeout(() => {
                // If we didn't got any response from tabs this means that there's no multiple Fiverr tabs at the moment.
                if (this._gotResponse[uid]) {
                    resolve(false);
                    return;
                }

                resolve(true);
            }, 600);
        });

    private _anotherTabIsOpen = (results: TabResult[]): boolean => results.some((result) => result.isFocused);

    private _currentTabShouldPlaySound = (results: TabResult[]): boolean => {
        const otherTabs = orderBy(results, 'createdAt', 'desc');
        const recentOtherTab = otherTabs[0];

        return this._createdAt > recentOtherTab.createdAt;
    };

    playSound = throttle(
        async (): Promise<void> => {
            const shouldPlaySound = await this._shouldPlaySound();

            if (shouldPlaySound) {
                NotificationSound.playSound();
            }
        },
        THROTTLE_SOUND,
        { trailing: false }
    );

    private _onBroadcastMessage = ({ data }: MessageEvent<BroadcastData>): void => {
        const { _tabId: tabId, _createdAt: createdAt } = this;

        const { type, callbackFnName, uid } = data;

        switch (type) {
            case ECHO_TABS: {
                if (!uid || !callbackFnName) {
                    break;
                }

                const isFocused = this.isFocused();

                this._channel?.postMessage({
                    type: ECHO_TABS_RESPONSE,
                    uid,
                    tabId,
                    createdAt,
                    callbackFnName,
                    isFocused,
                });
                break;
            }
            case ECHO_TABS_RESPONSE:
                if (!uid || !callbackFnName) {
                    break;
                }

                this._recordInQueue({
                    uid,
                    data,
                    callbackFnName,
                });
                break;
            default:
                break;
        }

        this._notifySubscribers(data);
    };

    private _notifySubscribers = (event: BroadcastData): void => {
        this._handlers.forEach((h) => h(event));
    };

    private _recordInQueue = ({ uid, data, callbackFnName }: QueueItem): void => {
        if (!this._queue[uid]) {
            this._queue[uid] = [];
        }

        this._queue[uid].push(data);

        if (!this._queueKickedOff[uid]) {
            this._queueKickedOff[uid] = true;

            // Queue all responses in window time of 500ms.
            setTimeout(() => {
                this._queueKickedOff[uid] = false;

                if (this._callbacks[uid] && this._callbacks[uid][callbackFnName]) {
                    this._callbacks[uid][callbackFnName](this._queue[uid] as unknown as TabResult[]);
                }

                this._queue[uid] = [];
            }, 500);
        }
    };
}
